import type { GeoJSONPoint } from "@sitescene/types";

import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useRef, useState } from "react";

import {
	getMapboxAccessToken,
	MAPBOX_DARK_STREET_STYLE,
	MAPBOX_LIGHT_STREET_STYLE,
} from "@/features/location-management/map/mapbox-constants";
import { useSettings } from "@/hooks/useSettings";
import { ContrastModes } from "@/types/settings";

export interface TrackMapSensor {
	geolocation?: GeoJSONPoint;
	id: string;
	name: string;
}

interface TrackMapProps {
	/** A camera to call out, e.g. the one under the pointer on the timeline. */
	highlighted?: string;
	onHover: (sensorId: string | undefined) => void;
	onSelect: (sensorId: string) => void;
	sensors: TrackMapSensor[];
	/** The sightings' cameras in time order; numbered on the map from 1. */
	stops: string[];
}

const PATH_SOURCE = "track-path";
const PATH_COLOR = "#7f56d9";
const MAX_ZOOM = 18;

/**
 * The track's cameras, with its path drawn between them in time order. Each
 * shows its name and how many visits the track made to it. The site's other
 * cameras are left off, so the map frames the path, and so are cameras
 * without a map position.
 */
export const TrackMap = ({
	highlighted,
	onHover,
	onSelect,
	sensors,
	stops,
}: TrackMapProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const mapRef = useRef<mapboxgl.Map | null>(null);
	const markersRef = useRef(new Map<string, HTMLElement>());
	const [loaded, setLoaded] = useState(false);
	// The latest handlers, for marker listeners created once per redraw
	const handlers = useRef({ onHover, onSelect });
	useEffect(() => {
		handlers.current = { onHover, onSelect };
	});

	const dark = useDarkMap();
	const onPath = new Set(stops);
	const placed = sensors.filter(
		(sensor) => sensor.geolocation && onPath.has(sensor.id),
	);
	const fitKey = placed.map((sensor) => sensor.id).join(",");

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		mapboxgl.accessToken = getMapboxAccessToken() ?? "";
		const map = new mapboxgl.Map({
			attributionControl: false,
			container,
			pitch: 0,
			projection: "mercator",
			style: dark ? MAPBOX_DARK_STREET_STYLE : MAPBOX_LIGHT_STREET_STYLE,
		});
		map.addControl(new mapboxgl.NavigationControl({ showCompass: false }));
		map.on("load", () => {
			map.addSource(PATH_SOURCE, {
				data: { features: [], type: "FeatureCollection" },
				type: "geojson",
			});
			map.addLayer({
				id: PATH_SOURCE,
				layout: { "line-cap": "round", "line-join": "round" },
				paint: {
					"line-color": PATH_COLOR,
					"line-dasharray": [2, 1.5],
					"line-width": 3,
				},
				source: PATH_SOURCE,
				type: "line",
			});
			setLoaded(true);
		});
		mapRef.current = map;
		return () => {
			map.remove();
			mapRef.current = null;
			setLoaded(false);
		};
	}, [dark]);

	// The path and the camera markers
	useEffect(() => {
		const map = mapRef.current;
		if (!map || !loaded) return;
		const elements = markersRef.current;
		const byId = new Map(placed.map((sensor) => [sensor.id, sensor]));

		const path = stops
			.filter((id, index) => byId.has(id) && stops[index - 1] !== id)
			.map((id) => byId.get(id)?.geolocation?.coordinates as [number, number]);
		(map.getSource(PATH_SOURCE) as mapboxgl.GeoJSONSource | undefined)?.setData(
			{
				geometry: { coordinates: path, type: "LineString" },
				properties: {},
				type: "Feature",
			},
		);

		const numbers = new Map<string, number[]>();
		stops.forEach((id, index) =>
			numbers.set(id, [...(numbers.get(id) ?? []), index + 1]),
		);
		const markers = placed.map((sensor) => {
			const visits = numbers.get(sensor.id) ?? [];
			const element = document.createElement("button");
			element.type = "button";
			element.title = `${sensor.name}: visit${visits.length === 1 ? "" : "s"} ${visits.join(", ")}`;
			element.setAttribute("aria-label", element.title);
			element.className =
				"relative flex h-7 min-w-7 items-center justify-center rounded-full border-2 border-white bg-brand-600 px-1.5 text-xs font-semibold text-white shadow";
			element.textContent = String(visits.length);
			// The name under the count, so the path reads without hovering
			const label = document.createElement("span");
			label.className =
				"pointer-events-none absolute top-full left-1/2 mt-1 -translate-x-1/2 rounded bg-white/90 px-1 text-[11px] font-medium whitespace-nowrap text-gray-900 shadow-sm";
			label.textContent = sensor.name;
			element.append(label);
			element.addEventListener("mouseenter", () =>
				handlers.current.onHover(sensor.id),
			);
			element.addEventListener("mouseleave", () =>
				handlers.current.onHover(undefined),
			);
			element.addEventListener("click", () =>
				handlers.current.onSelect(sensor.id),
			);
			elements.set(sensor.id, element);
			return new mapboxgl.Marker({ element })
				.setLngLat(sensor.geolocation?.coordinates as [number, number])
				.addTo(map);
		});
		return () => {
			markers.forEach((marker) => marker.remove());
			elements.clear();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- placed is derived from sensors
	}, [loaded, sensors, stops]);

	// Frame the cameras once they're known
	useEffect(() => {
		const map = mapRef.current;
		if (!map || !loaded || !fitKey) return;
		const bounds = new mapboxgl.LngLatBounds();
		for (const sensor of placed) {
			bounds.extend(sensor.geolocation?.coordinates as [number, number]);
		}
		map.fitBounds(bounds, { animate: false, maxZoom: MAX_ZOOM, padding: 48 });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- refit only when the set of cameras changes
	}, [fitKey, loaded]);

	useEffect(() => {
		outlineMarker(markersRef.current, highlighted);
	}, [highlighted, loaded, sensors, stops]);

	if (placed.length === 0) {
		return (
			<p className="text-sm text-tertiary">
				None of this track's cameras has a map position, so there's no map.
			</p>
		);
	}

	return (
		<div
			className="h-80 w-full overflow-hidden rounded-md border border-secondary"
			ref={containerRef}
		/>
	);
};

/** Rings the highlighted camera's marker, and only it. */
const outlineMarker = (
	elements: ReadonlyMap<string, HTMLElement>,
	highlighted: string | undefined,
) => {
	for (const [id, element] of elements) {
		element.style.outline = id === highlighted ? `3px solid ${PATH_COLOR}` : "";
		element.style.outlineOffset = id === highlighted ? "2px" : "";
	}
};

/** Whether the app is showing its dark theme. */
const useDarkMap = (): boolean => {
	const { contrastMode } = useSettings();
	if (contrastMode === ContrastModes.DARK) return true;
	if (contrastMode === ContrastModes.LIGHT) return false;
	return (
		typeof window !== "undefined" &&
		window.matchMedia("(prefers-color-scheme: dark)").matches
	);
};
